import React, { useMemo } from "react";
import { getSizedImage } from "@/helpers/utils";
import { ProductCardList } from "@/features/product/ProductCardList";
import { productSelect, variantSelect } from "@/store";
import { ProductCardProduct } from "@/features/product/ProductCard";
import { ShoppableProductCardProduct } from "@/features/product/ShoppableProductCard";
import { storefrontApi } from "@/services/storefront";

export interface RelatedProductsProps {
  numProducts?: number;
  condensedView?: boolean;
  shoppable?: boolean;
  layout?: "grid" | "carousel" | "flex";
  relatedProducts?: any[];
}
export const RelatedProducts = ({
  shoppable = true,
  numProducts = 50,
  layout = "carousel",
  condensedView = false,
  relatedProducts,
}: RelatedProductsProps) => {
  const skeletonCards = useMemo<ProductCardProduct[]>(() => {
    return Array(numProducts).fill({
      imageHeight: 440,
      imageWidth: 549,
      featuredImage: true,
      featuredText: !condensedView,
      type: true,
      ratings: true,
      url: !condensedView,
      title: true,
      badge: true,
      price: true,
      size: true,
    });
  }, [numProducts, condensedView]);

  const handles = (relatedProducts ?? []).map((x) => x.handle.toString());
  const collection = storefrontApi.useHandles(handles, "products");

  const productCards = useMemo<
    ShoppableProductCardProduct[] | undefined
  >(() => {
    if (collection.data === undefined) {
      return undefined;
    }

    const products = collection.data.all.filter((x) => !x.hidden);
    return products.map((product, i) => {
      const variant = productSelect.defaultVariant(product);
      const featuredText =
        product.featuredQuote && !condensedView
          ? `“${product.featuredQuote}”`
          : undefined;
      return {
        id: variant.id,
        featuredImage: {
          src: getSizedImage(productSelect.featuredImage(product).url, "x440"),
          height: 440,
          width: 549,
          loading: "lazy",
        },
        badge: product.badge,
        featuredText,
        type: product.type,
        url: productSelect.url(product),
        title: product.title,
        size: variantSelect.sizeFormat(variant),
        compareAtPrice: variant.compareAtPrice,
        price: shoppable ? variant.price : variantSelect.lowestPrice(variant),
        product,
        variant,
        collectionProducts: [],
      };
    });
  }, [collection.data, condensedView, shoppable]);

  const data =
    productCards && shoppable
      ? ({
          shoppable: true,
          products: productCards,
        } as const)
      : ({
          shoppable: false,
          products: productCards ?? skeletonCards,
        } as const);

  return <ProductCardList layout={layout} {...data} />;
};
